import { ChevronUp, LandPlot, LogOut, UserMinus, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Can } from "@/components/can";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../../api/use-claim";
import { useSoftDeletePassengers, useUpdatePassenger } from "../../api/use-claim-mutations";
import { PnrDisplayDialog } from "../pnr-display/pnr-display-dialog";
import { AddPassengerDialog } from "./add-passenger-dialog";
import { ExtractPassengersModal } from "./extract-passengers-modal";
import { FullReservationItinerary } from "./full-reservation-itinerary";
import { PassengerBirthdateActions } from "./passenger-birthdate-actions";
import { PassengerEmailActions } from "./passenger-email-actions";
import { groupPassengersByFamilies, isValidEmail, type PassengerRow } from "./passenger-grouping";
import { ReservationSummary } from "./reservation-summary";
import { TicketHistory } from "./ticket-history";

/** Claim states allowing the inline passenger email input (PassengerTable.vue). */
const EMAIL_EDITABLE_STATES = [
  "CLAIM_VALIDATION.CHOICE_OF_CONTACT_MODE",
  "CLAIM_VALIDATION.WAIT_FOR_ELIGIBILITY_CHECK",
  "CLAIM_VALIDATION.WAIT_FOR_PAX_EMAIL_FROM_TRAVEL_AGENCY",
  "CLAIM_VALIDATION.WAIT_FOR_PAX_EMAIL_FROM_ENTERPRISE",
];

/** Port of PassengerTable.vue — reservation info + passengers grouped by family. */
export function PassengerTable({ claim }: { claim: ClaimWithRelations }) {
  const [selectedPassengers, setSelectedPassengers] = useState<Set<string>>(new Set());
  const [addPassengersModalOpen, setAddPassengersModalOpen] = useState(false);
  const [extractPassengersModalOpen, setExtractPassengersModalOpen] = useState(false);
  const [usersEmail, setUsersEmail] = useState<Record<number, string>>({});
  const [detailedReservationOpen, setDetailedReservationOpen] = useState(
    claim.claimState.type !== "CLAIM_VALIDATION.WAIT_FOR_PAX_EMAIL_FROM_TRAVEL_AGENCY",
  );

  const updatePassenger = useUpdatePassenger(claim.id);
  const softDeletePassengersMutation = useSoftDeletePassengers(claim.id);

  const passengersByFamilies = groupPassengersByFamilies(claim);
  const allPassengers = passengersByFamilies.flat();

  // Mirrors the Vue watcher clearing the selection when the passenger list changes.
  // Keyed on a stable ids string rather than the `usersClaims` array reference — React
  // Query's structural sharing can hand back a new array on refetch even when the
  // content is unchanged, which would otherwise retrigger this effect on every render.
  const usersClaimsIds = claim.usersClaims
    .map((userClaim) => userClaim.id)
    .sort()
    .join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: usersClaimsIds is only a change trigger, not read in the body
  useEffect(() => {
    setSelectedPassengers(new Set());
  }, [usersClaimsIds]);

  const selectedUsersIds = Array.from(selectedPassengers);
  const selectedPassengersData = allPassengers
    .filter(({ passenger }) => selectedUsersIds.some((sId) => sId === passenger.id))
    .map(({ passenger }) => passenger);

  function toggleSelection(id: string) {
    setSelectedPassengers((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function getRowBackgroundColor(userId: string) {
    if (selectedPassengers.has(userId)) {
      return "bg-amber-100 hover:bg-amber-100";
    }
    return "bg-white";
  }

  function handleUpdatePassenger(userSegmentId: number) {
    const email = usersEmail[userSegmentId];
    if (isValidEmail(email)) {
      updatePassenger.mutate({ userSegmentId, email: email.trim() });
    }
  }

  function softDeletePassengers() {
    const usersClaimsIds = claim.usersClaims
      .filter((uc) => selectedPassengers.has(uc.userId))
      .map(({ id }) => id);
    softDeletePassengersMutation.mutate({ usersClaimsIds });
  }

  const canEditEmail = EMAIL_EDITABLE_STATES.includes(claim.claimState.type);

  function renderRow(element: PassengerRow) {
    const birthDate = element.passenger.birthDate ? new Date(element.passenger.birthDate) : null;
    const hasValidBirthDate = Boolean(birthDate && !Number.isNaN(birthDate.getTime()));
    const userSegmentId = element.id;

    return (
      <tr
        key={element.userClaimId}
        onClick={() => toggleSelection(element.passenger.id)}
        className={cn("group cursor-pointer hover:bg-gray-100", getRowBackgroundColor(element.passenger.id))}
      >
        <td className="whitespace-nowrap py-4">
          <div className="flex items-center text-left">
            <div className="ml-6">
              <div className="text-sm font-medium text-gray-900">{element.passenger.civility}</div>
              <div className="text-sm text-gray-500">
                {element.passenger.contact ? (
                  <div className="flex items-center gap-1">
                    <span>{element.passenger.contact}</span>
                    <PassengerEmailActions claimId={claim.id} row={element} />
                  </div>
                ) : (
                  // biome-ignore lint/a11y/noStaticElementInteractions: mirrors the Vue @click.stop wrapper
                  // biome-ignore lint/a11y/useKeyWithClickEvents: mirrors the Vue @click.stop wrapper
                  <div onClick={(event) => event.stopPropagation()}>
                    <div className="mt-2">
                      {canEditEmail && userSegmentId !== null ? (
                        <>
                          <Input
                            size="sm"
                            value={usersEmail[userSegmentId] ?? ""}
                            onChange={(event) =>
                              setUsersEmail((current) => ({
                                ...current,
                                [userSegmentId]: event.target.value,
                              }))
                            }
                            onKeyUp={(event) => {
                              if (event.key === "Enter") {
                                handleUpdatePassenger(userSegmentId);
                              }
                            }}
                            autoComplete="off"
                            type="email"
                            placeholder="Saisir l'email du passager"
                          />
                          {isValidEmail(usersEmail[userSegmentId]) ? (
                            <Button
                              size="sm"
                              className="mt-2"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleUpdatePassenger(userSegmentId);
                              }}
                            >
                              Enregistrer
                            </Button>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-sm italic text-gray-400">—</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {hasValidBirthDate && birthDate ? (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <span>Né le: {birthDate.toLocaleDateString()}</span>
                  <PassengerBirthdateActions claimId={claim.id} row={element} />
                </div>
              ) : (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <PassengerBirthdateActions claimId={claim.id} row={element} />
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-left">
          <div className="text-sm text-gray-900">{element.booking.flightIdentifier}</div>
          <div className="text-sm text-gray-500">{element.booking.airlineName}</div>
          <div className="text-xs">Date de réservation : {element.booking.bookingDate}</div>
          <div>
            {element.booking.isCodeshare ? (
              <span className="inline-flex items-center rounded bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800">
                <svg
                  className="mr-1.5 h-2 w-2 text-indigo-400"
                  fill="currentColor"
                  viewBox="0 0 8 8"
                  aria-hidden="true"
                >
                  <circle cx="4" cy="4" r="3" />
                </svg>
                Codeshare
              </span>
            ) : (
              <span className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                Compagnie effective
              </span>
            )}
          </div>
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
          <Checkbox
            checked={selectedPassengers.has(element.passenger.id)}
            onCheckedChange={() => toggleSelection(element.passenger.id)}
            onClick={(event) => event.stopPropagation()}
            name="select-passenger"
            aria-label={`Sélectionner ${element.passenger.civility}`}
          />
        </td>
      </tr>
    );
  }

  return (
    <>
      <AddPassengerDialog
        claimId={claim.id}
        open={addPassengersModalOpen}
        onClose={() => setAddPassengersModalOpen(false)}
      />

      <ExtractPassengersModal
        claimId={claim.id}
        currentClaimTypeName={claim.claimType.name}
        users={selectedPassengersData}
        open={extractPassengersModalOpen}
        onClose={() => setExtractPassengersModalOpen(false)}
      />

      <div className="bg-white px-6 py-5 text-left">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-medium leading-6 text-gray-900">Réservation</h2>
            <p className="mt-1 max-w-2xl text-sm font-normal text-gray-500">
              Liste des passagers impactés et leur itinéraire complet.
            </p>
          </div>
          <PnrDisplayDialog claimId={claim.id} />
        </div>
        <ReservationSummary claim={claim} />

        {/* No PASSIVE_OFFICE gating (unlike PassengerTable.vue): partner feeds (Digitrips,
            TECH-530/607) register tickets for passive offices too; the endpoint reads the DB
            and TicketHistory has a proper empty state for reservations without tickets. */}
        <div className="mt-12 px-2">
          <h2 className="text-lg font-medium leading-6 text-gray-900">Billets électroniques</h2>
          <p className="mt-1 max-w-2xl text-sm font-normal text-gray-500">
            E-Tickets pour chaque passager et leurs statuts.
          </p>
          <div className="w-full py-2">
            <TicketHistory
              claimId={claim.id}
              impactedFlightId={claim.legalSpecification.impactedSegment.flightId}
            />
          </div>
        </div>

        <div className="-mx-6">
          <div className="w-full py-4">
            <div className="mx-auto w-full rounded-2xl bg-white p-2">
              <Collapsible open={detailedReservationOpen} onOpenChange={setDetailedReservationOpen}>
                <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-left text-sm font-medium text-blue-900 hover:bg-blue-100 focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                  <span className="inline-flex items-center">
                    <span className="mr-3">
                      <ChevronUp
                        className={cn(
                          "h-5 w-5 text-blue-500",
                          detailedReservationOpen && "rotate-180 transform",
                        )}
                      />
                    </span>
                    Réservation détaillée
                    <span className="ml-2 text-xs font-normal italic">
                      (Cliquer pour {detailedReservationOpen ? "fermer" : "ouvrir"})
                    </span>
                  </span>
                  <LandPlot />
                </CollapsibleTrigger>
                <CollapsibleContent className="pb-2 pt-4 text-sm text-gray-500">
                  <FullReservationItinerary
                    claimId={claim.id}
                    reservationId={claim.legalSpecification.impactedSegment.reservationId}
                    impactedSegmentId={claim.legalSpecification.impactedSegmentId}
                    claimType={claim.claimType.name}
                  />
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div>
              {passengersByFamilies.map((passengerFamily) => (
                <table
                  key={passengerFamily[0].userClaimId}
                  className="my-3 min-w-full divide-y divide-gray-200 overflow-hidden border-y border-gray-200 shadow sm:rounded-lg"
                >
                  <thead className="bg-stone-100">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        {passengerFamily.length === 1
                          ? "Passager"
                          : `Famille ${passengerFamily[0].familyName}`}
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Réservation
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Edit</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="border-t-1 divide-y divide-gray-200 bg-white">
                    {passengerFamily.map((element) => renderRow(element))}
                  </tbody>
                </table>
              ))}
            </div>
          </div>
        </div>
      </div>

      {selectedPassengers.size > 0 ? (
        <div className="mt-6 flex flex-1 justify-end gap-x-3 px-6">
          <Can permissions={UPCLAIM_ROLES}>
            <Button onClick={() => setExtractPassengersModalOpen(true)}>
              <LogOut aria-hidden="true" />
              Extraire {selectedPassengers.size} passager(s)
            </Button>
          </Can>
          <Button variant="destructive" onClick={softDeletePassengers}>
            <UserMinus aria-hidden="true" />
            Supprimer {selectedPassengers.size} passager(s)
          </Button>
        </div>
      ) : (
        <div className="mt-6 flex flex-1 justify-end px-6">
          <Button onClick={() => setAddPassengersModalOpen(true)}>
            <UserPlus aria-hidden="true" />
            Ajouter un passager
          </Button>
        </div>
      )}
    </>
  );
}
